import {useState} from 'react';
import {Button} from '@nlabs/gothamjs';

import ufatProjectsCityHero from '../../assets/ufat-projects-city-hero.png';
import {Hero} from '../../components/Hero';
import {Page} from '../../components/Page';
import {RotatingHeroTitle} from '../../components/RotatingHeroTitle';
import {Section} from '../../components/Section';
import {ABOUT_HERO_TITLES} from '../../data/heroTitles';
import {navigate} from '../../lib/navigation';
import {button, buttonLight, buttonOutlineDark, copyBlockWide, reveal} from '../../lib/styles';
import {Route} from '../../types/routes';

const companyOverviewParagraphs = [
  'United Foundation for Arts & Technology is a creative technology company focused on building modern experiences across storytelling, publishing, digital platforms, and emerging technology. Our work spans original books, visual media, mobile applications, web platforms, AI-integrated systems, APIs, SaaS ecosystems, and scalable digital infrastructure designed for a rapidly evolving world. By combining creativity with deep technical architecture experience, we create products that are both imaginative and built for long-term growth.',
  'At the core of UFAT is a belief that storytelling and technology are no longer separate industries. Modern audiences move fluidly between books, media, applications, communities, and intelligent digital experiences, and we believe the companies shaping the future will understand how to connect them together seamlessly. Whether developing immersive fictional worlds, launching creative media, building intelligent business systems, or engineering modern platforms, our goal is to create experiences that feel unified, cinematic, and forward-thinking.',
  'As technology continues to evolve through AI, automation, connected platforms, and new forms of digital interaction, UFAT is focused on building for what comes next. We see the future as a space where creativity, intelligent systems, and scalable infrastructure work together to unlock entirely new ways for people to learn, create, communicate, and experience stories. Every project we build is part of a larger vision: creating a modern ecosystem where imagination and technology evolve side by side.'
];

const highlightTabs = [
  {
    id: 'identity',
    label: 'Identity',
    title: 'We Are UFAT',
    subtitle: 'Building stories, worlds, and digital experiences designed for what comes next.',
    paragraphs: [
      'UFAT was created from a belief that storytelling and technology should not live in separate worlds. The books we read, the media we experience, and the platforms we use every day are becoming increasingly connected, and we believe the future belongs to creators who understand how to blend imagination with modern digital experiences.',
      'Our work spans original fiction, children\'s literature, immersive creative media, intelligent digital platforms, AI-powered systems, and evolving online ecosystems designed for modern audiences. Whether someone discovers us through a story, an application, a digital experience, or a future platform still taking shape, our goal remains the same: create worlds that people genuinely want to explore.'
    ]
  },
  {
    id: 'focus',
    label: 'Focus',
    title: 'Creative technology',
    subtitle: 'A connected studio model for publishing, software, and media.',
    paragraphs: [
      'We build across story, product, platform, and infrastructure because modern audiences do not experience those categories in isolation.',
      'Each initiative is designed to connect imagination with practical systems, giving creative ideas room to grow into long-term digital ecosystems.'
    ]
  },
  {
    id: 'future',
    label: 'Future',
    title: 'Designed for what comes next',
    subtitle: 'Imaginative work with scalable foundations.',
    paragraphs: [
      'UFAT looks for places where creative media, intelligent systems, and connected communities can reinforce one another.',
      'That means building stories that can expand, platforms that can adapt, and tools that help people create, learn, communicate, and explore.'
    ]
  }
];

const aboutSections = [
  {
    title: 'More Than Stories',
    subtitle: 'Creating immersive experiences across print, media, and digital platforms.',
    paragraphs: [
      'At the heart of UFAT is a passion for worldbuilding. We are inspired by the idea that a story should not simply exist on a page, but continue to live through art, technology, community, and connected experiences that extend beyond traditional formats.',
      'From hardcover children\'s books and paperback novels to digital releases and interactive media, we create projects designed to feel cinematic, immersive, and emotionally memorable. Our stories explore imagination, mystery, adventure, science fiction, fantasy, and the evolving relationship between humanity and technology. Every release is approached as part of a larger creative universe rather than a single isolated product.',
      'As new technologies continue to reshape how audiences interact with media, UFAT is focused on building experiences that evolve alongside them. We see a future where storytelling flows naturally between books, digital platforms, intelligent systems, and immersive online communities.'
    ]
  },
  {
    title: 'Built for the Next Generation',
    subtitle: 'Intelligent platforms and evolving digital ecosystems.',
    paragraphs: [
      'Alongside our creative work, UFAT develops a growing ecosystem of modern digital products and connected technologies designed to support creators, readers, businesses, and evolving online experiences. Our platforms incorporate scalable architecture, AI integration, modern web technologies, APIs, SaaS ecosystems, and intelligent digital workflows built for long-term adaptability.',
      'Rather than simply building technology for technology\'s sake, we focus on creating systems that feel useful, intuitive, and connected to the experiences people already care about. From content platforms and creative ecosystems to future-facing AI tools and digital infrastructure, our goal is to create products that help shape how people discover, create, learn, and interact online.',
      'We believe the future of technology is not cold or disconnected. It is creative, immersive, intelligent, and deeply human.'
    ]
  },
  {
    title: 'For Authors & Creators',
    subtitle: 'A modern home for original voices and new worlds.',
    paragraphs: [
      'UFAT is actively building relationships with writers, illustrators, and creators who want to bring ambitious ideas to life. We believe great stories can come from anywhere, and we are passionate about helping creators develop, launch, and grow their work through modern publishing and connected digital experiences.',
      'Whether you are creating children\'s books, science fiction, fantasy, educational content, or entirely new forms of storytelling, we are interested in projects that push beyond traditional boundaries and create meaningful experiences for audiences.',
      'Our vision is to build more than a publishing label. We are building a long-term creative ecosystem where stories, creators, technology, and audiences evolve together.'
    ]
  },
  {
    title: 'Looking Forward',
    subtitle: 'Imagination and technology evolving side by side.',
    paragraphs: [
      'The future is changing rapidly through AI, connected platforms, immersive media, and new forms of digital interaction. UFAT exists to explore that future creatively and responsibly through experiences that inspire curiosity, innovation, and exploration.',
      'Everything we create, whether physical or digital, is part of a larger vision: a connected universe where storytelling, intelligent systems, and modern technology work together to create experiences that feel alive.'
    ]
  }
];

const AboutNarrativeSection = ({
  index,
  paragraphs,
  subtitle,
  title
}: {
  index: number;
  paragraphs: string[];
  subtitle: string;
  title: string;
}) => (
  <Section className={`nx-section-about-story ${index % 2 === 1 ? 'nx-section-about-story-alt' : ''}`} title={title}>
    <div className="grid max-w-[980px] gap-[22px]">
      <p className="-mt-3 mb-2 max-w-[820px] text-[clamp(1.25rem,2.2vw,1.75rem)] font-bold leading-[1.35] text-[var(--nx-ink)]">{subtitle}</p>
      {paragraphs.map((paragraph) => <p key={paragraph} className="m-0 text-[clamp(1.02rem,1.24vw,1.2rem)] leading-[1.82] text-[rgba(52,64,84,.88)]">{paragraph}</p>)}
    </div>
  </Section>
);

const AboutOverviewSection = () => (
  <Section className="nx-section-overview py-[clamp(72px,10vw,132px)]" title="Company overview">
    <div className="grid items-start gap-[clamp(30px,5vw,72px)] min-[841px]:grid-cols-[minmax(0,1.35fr)_minmax(280px,.65fr)]">
      <div className={`${copyBlockWide} gap-[26px]`}>
        {companyOverviewParagraphs.map((paragraph) => (
          <p key={paragraph} className="m-0 text-[clamp(1.02rem,1.24vw,1.22rem)] leading-[1.82] text-[rgba(52,64,84,.86)]">{paragraph}</p>
        ))}
        <div className="mt-2 flex flex-wrap gap-3">
          <Button className={button} onClick={() => navigate('/publishing')}>Publishing</Button>
          <Button className={buttonOutlineDark} onClick={() => navigate('/projects')}>View Projects</Button>
        </div>
      </div>
      <div className={`${reveal} sticky top-24 grid gap-3 rounded-[var(--nx-radius)] border border-[rgba(16,22,36,.08)] bg-[linear-gradient(145deg,rgba(255,255,255,.86),rgba(255,255,255,.48)),radial-gradient(ellipse_at_20%_16%,rgba(88,203,217,.22),transparent_42%),radial-gradient(ellipse_at_88%_88%,rgba(194,135,224,.2),transparent_38%)] bg-blend-normal p-[clamp(18px,3vw,28px)] max-[840px]:static`} aria-hidden="true">
        <span className="rounded-[var(--nx-radius)] border border-[rgba(16,22,36,.08)] bg-white/70 p-[18px] text-[clamp(1rem,1.4vw,1.2rem)] font-extrabold text-[var(--nx-ink)]">Storytelling</span>
        <span className="rounded-[var(--nx-radius)] border border-[rgba(16,22,36,.08)] bg-white/70 p-[18px] text-[clamp(1rem,1.4vw,1.2rem)] font-extrabold text-[var(--nx-ink)]">AI Systems</span>
        <span className="rounded-[var(--nx-radius)] border border-[rgba(16,22,36,.08)] bg-white/70 p-[18px] text-[clamp(1rem,1.4vw,1.2rem)] font-extrabold text-[var(--nx-ink)]">Publishing</span>
        <span className="rounded-[var(--nx-radius)] border border-[rgba(16,22,36,.08)] bg-white/70 p-[18px] text-[clamp(1rem,1.4vw,1.2rem)] font-extrabold text-[var(--nx-ink)]">Digital Platforms</span>
      </div>
    </div>
  </Section>
);

const AboutHighlightsSection = () => {
  const [activeTabId, setActiveTabId] = useState(highlightTabs[0].id);
  const activeTab = highlightTabs.find((tab) => tab.id === activeTabId) ?? highlightTabs[0];

  return (
    <Section className="nx-section-about-story-alt" title="Highlights">
      <div className="grid gap-[22px] rounded-[var(--nx-radius)] border border-[rgba(16,22,36,.08)] bg-white/70 p-[clamp(18px,4vw,34px)] backdrop-blur">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="About highlights">
          {highlightTabs.map((tab) => (
            <button
              aria-controls={`about-highlight-${tab.id}`}
              aria-selected={tab.id === activeTabId}
              className={`min-h-11 rounded-[var(--nx-radius)] border px-4 text-sm font-bold transition-colors ${tab.id === activeTabId ? 'border-[var(--nx-ink)] bg-[var(--nx-ink)] text-white' : 'border-[rgba(16,22,36,.16)] bg-white/60 text-[rgba(16,22,36,.72)] hover:text-[var(--nx-ink)]'}`}
              id={`about-highlight-tab-${tab.id}`}
              key={tab.id}
              onClick={() => setActiveTabId(tab.id)}
              role="tab"
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div
          aria-labelledby={`about-highlight-tab-${activeTab.id}`}
          className="grid max-w-[900px] gap-[18px]"
          id={`about-highlight-${activeTab.id}`}
          role="tabpanel"
        >
          <div>
            <h3 className="mb-3 mt-0 text-[clamp(1.7rem,3vw,2.6rem)] leading-[1.05] text-[var(--nx-ink)]">{activeTab.title}</h3>
            <p className="m-0 max-w-[780px] text-[clamp(1.08rem,1.8vw,1.35rem)] font-bold leading-[1.4] text-[rgba(16,22,36,.78)]">{activeTab.subtitle}</p>
          </div>
          {activeTab.paragraphs.map((paragraph) => (
            <p key={paragraph} className="m-0 text-[clamp(1rem,1.2vw,1.14rem)] leading-[1.78] text-[rgba(52,64,84,.88)]">{paragraph}</p>
          ))}
        </div>
      </div>
    </Section>
  );
};

export const About = ({route}: {route: Route}) => {
  return (
    <Page route={route}>
      <Hero
        actions={(
          <>
            <Button className={button} onClick={() => navigate('/projects')}>View Projects</Button>
            <Button className={buttonLight} onClick={() => navigate('/support')}>Support</Button>
          </>
        )}
        backgroundImage={ufatProjectsCityHero}
        imageLabel="About placeholder image"
        kicker="About UFAT"
        subtitle="UFAT creates imaginative media and intelligent digital products through a unified creative and technical approach."
        title={<RotatingHeroTitle items={ABOUT_HERO_TITLES} />}
        tone="violet"
      />
      <AboutOverviewSection />
      <AboutHighlightsSection />
      {aboutSections.map((section, index) => <AboutNarrativeSection key={section.title} index={index} {...section} />)}
    </Page>
  );
};
